import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useMe } from '../context/MeContext.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';

export function CourseSwitcher({ activeCourseSlug }: { activeCourseSlug: string }) {
  const { me } = useMe();
  const navigate = useNavigate();

  if (!me || me.courses.length <= 1) return null;

  const active = me.courses.find((c) => c.courseSlug === activeCourseSlug) ?? me.courses[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex items-center gap-2 rounded-pill border border-border bg-bg-card px-3 py-1.5',
          'text-sm font-medium text-fg transition-colors duration-[150ms]',
          'hover:border-greeniq-500/40 hover:bg-bg-hover',
          'focus:outline-none focus-visible:shadow-focus'
        )}
      >
        <span className="size-1.5 rounded-full bg-greeniq-500 shadow-glow-green" aria-hidden />
        <span className="max-w-[180px] truncate">{active.courseName}</span>
        <ChevronDown size={14} className="text-fg-muted" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>Switch course</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {me.courses.map((c) => {
          const isActive = c.courseSlug === activeCourseSlug;
          return (
            <DropdownMenuItem
              key={c.courseSlug}
              onSelect={() => navigate(`/app/${c.courseSlug}`)}
              className={cn('flex items-start gap-2 px-2 py-2', isActive && 'bg-bg-hover')}
            >
              <Check
                size={14}
                className={cn(
                  'mt-0.5 shrink-0',
                  isActive ? 'text-greeniq-500' : 'text-transparent'
                )}
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-fg">{c.courseName}</span>
                <span className="text-[11px] uppercase tracking-wider text-fg-muted">
                  {c.organizationName}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
